import { useState } from 'react';
import type { TrackingEvent, EventType } from '../../../utils/analytics';

interface EventsListProps {
  events: TrackingEvent[];
}

const TYPE_CONFIG: Record<EventType, { label: string; icon: string; color: string; badge: string }> = {
  page_view:      { label: 'Visita',        icon: 'ri-eye-line',           color: 'text-[#A5A5A5]',  badge: 'bg-[#A5A5A5]/10 text-[#A5A5A5]' },
  whatsapp_click: { label: 'WhatsApp',      icon: 'ri-whatsapp-fill',      color: 'text-green-500',  badge: 'bg-green-500/10 text-green-600' },
  phone_click:    { label: 'Telefono',      icon: 'ri-phone-fill',         color: 'text-[#7FCDCD]',  badge: 'bg-[#7FCDCD]/10 text-[#5FBABA]' },
  preventivo_cta: { label: 'Click CTA',     icon: 'ri-cursor-fill',        color: 'text-amber-500',  badge: 'bg-amber-500/10 text-amber-600' },
  form_submit:    { label: 'Form inviato',  icon: 'ri-send-plane-fill',    color: 'text-orange-500', badge: 'bg-orange-500/10 text-orange-600' },
  nav_click:      { label: 'Navigazione',   icon: 'ri-compass-3-line',     color: 'text-[#555555]',  badge: 'bg-[#555555]/10 text-[#555555]' },
};

const SOURCE_LABELS: Record<string, string> = {
  hero:         'Hero',
  navbar:       'Navbar',
  form_section: 'Sezione Form',
  mobile_bar:   'Barra Mobile',
  footer:       'Footer',
  float_button: 'Pulsante Flottante',
  page:         'Pagina',
};

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const ALL_TYPES = ['all', 'page_view', 'nav_click', 'whatsapp_click', 'phone_click', 'preventivo_cta', 'form_submit'] as const;
type FilterType = typeof ALL_TYPES[number];

export default function EventsList({ events }: EventsListProps) {
  const [filter, setFilter] = useState<FilterType>('all');

  const filtered = filter === 'all' ? events.slice(0, 100) : events.filter((e) => e.type === filter).slice(0, 100);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
      {/* Header */}
      <div className="px-5 md:px-7 pt-5 pb-4 border-b border-[#F6F6F6] flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-black text-[#000000]" style={{ fontFamily: "'Raleway', sans-serif" }}>
          Ultime azioni
        </h3>
        <div className="flex items-center gap-1.5 flex-wrap">
          {ALL_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                filter === t
                  ? 'bg-[#000000] text-white'
                  : 'bg-[#F6F6F6] text-[#A5A5A5] hover:text-[#000000]'
              }`}
            >
              {t === 'all' ? 'Tutti' : TYPE_CONFIG[t as EventType].label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="ri-inbox-2-line text-3xl text-[#E5E5E5] mb-3" />
          <p className="text-[#A5A5A5] text-sm">Nessun evento registrato</p>
        </div>
      ) : (
        <div className="divide-y divide-[#F6F6F6]">
          {filtered.map((event) => {
            const cfg = TYPE_CONFIG[event.type];
            return (
              <div key={event.id} className="flex items-center gap-4 px-5 md:px-7 py-3.5 hover:bg-[#FAFAFA] transition-colors">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.badge}`}>
                  <i className={`${cfg.icon} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                    {event.source && (
                      <span className="text-xs text-[#A5A5A5]">
                        da {SOURCE_LABELS[event.source] ?? event.source}
                      </span>
                    )}
                  </div>
                  {event.label && (
                    <p className="text-xs text-[#555555] mt-0.5 truncate">{event.label}</p>
                  )}
                </div>
                <span className="text-[10px] text-[#A5A5A5] flex-shrink-0 whitespace-nowrap">
                  {formatTime(event.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
